/**
 * TagForm Component
 * 
 * Reusable form for creating and editing tags.
 * 
 * Features:
 * - Name and color inputs
 * - Validation and error handling
 * - Color picker integration
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see specs/003-categorisation-feature/spec.md
 */

import React, { useState } from 'react';
import { Tag, CreateTagInput, UpdateTagInput } from '../../../../types/tag.types';
import { ColorPicker } from './ColorPicker';

interface TagFormProps {
  tag?: Tag; // If provided, this is an edit form
  onSubmit: (data: CreateTagInput | UpdateTagInput) => void;
  onCancel: () => void;
  loading?: boolean;
}

interface TagFormState {
  name: string;
  color: string;
}

export function TagForm({
  tag,
  onSubmit,
  onCancel,
  loading = false
}: TagFormProps): React.ReactElement {
  const isEdit = !!tag;

  const initialState: TagFormState = {
    name: tag?.name ?? '',
    color: tag?.color ?? '#3B82F6', // Default blue color
  };

  const [formData, setFormData] = useState<TagFormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = <K extends keyof TagFormState>(field: K, value: TagFormState[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as string]) {
      setErrors(prev => ({ ...prev, [field as string]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.name || formData.name.trim().length === 0) {
      newErrors.name = 'Tag name is required';
    } else if (formData.name.length > 50) {
      newErrors.name = 'Tag name must be 50 characters or less';
    }

    // Color validation
    if (!formData.color || !/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      newErrors.color = 'Tag color must be a valid hex color code (e.g., #FF5733)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      const submitData: CreateTagInput | UpdateTagInput = {
        name: formData.name.trim(),
        color: formData.color,
      };

      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Name */}
      <div>
        <label htmlFor="tag-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Tag Name <span className="text-red-500">*</span>
        </label>
        <input
          id="tag-name"
          type="text"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          className={`mt-1 block w-full rounded-lg border ${
            errors.name
              ? 'border-red-300 dark:border-red-700'
              : 'border-gray-300 dark:border-gray-600'
          } bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
          placeholder="e.g., Weekly, Monthly, Work"
          aria-required="true"
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? 'tag-name-error' : undefined}
        />
        {errors.name && (
          <p id="tag-name-error" className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.name}
          </p>
        )}
      </div>

      {/* Color */}
      <div>
        <ColorPicker
          value={formData.color}
          onChange={(color) => handleInputChange('color', color)}
          label="Color"
        />
        {errors.color && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
            {errors.color}
          </p>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : isEdit ? 'Update Tag' : 'Create Tag'}
        </button>
      </div>
    </form>
  );
}

